// admin/src/screens/BookEditor.jsx — draft → validate → activate (D1-DESIGN §6.4), with TWO
// views over the ONE lifecycle route set:
//   • Form  — a friendly per-main / per-sub editor (labels, factors, allowances, positions,
//             match routing). Retuning without hand-editing JSON.
//   • JSON  — the honest, complete assembled-book text (every field; wholesale restructuring).
// Both serialize to the SAME body PUT /v1/admin/books/:version takes (lib/book-form.js). The
// seam's book-lint is the single validator — we never re-implement it; we surface it.
//
// Immutability made physical: only a DRAFT is editable. An active/retired version is history
// forever (a job pinned to it settles under its rows) — rendered read-only, with the plain
// "draft a new version to retune" message. The server refuses a write anyway; we don't fight it.

import React, { useEffect, useMemo, useState } from 'react';
import { navigate } from '@common/router.js';
import { useAdmin } from '../App.jsx';
import { Banner, Field, fmtInt } from '../lib/ui.jsx';
import {
  cloneBook, serializeBook, sameContent, blankMain, blankSub,
  MODE_OPTIONS, POSITION_OPTIONS, EXTRA_MODE_OPTIONS, PER_UNIT_OPTIONS, MATCH_KEYS,
  numOrNull, arrFromCsv, csvFromArr,
} from '../lib/book-form.js';

export default function BookEditor({ version }) {
  const { api } = useAdmin();
  const [book, setBook] = useState(null);
  const [draft, setDraft] = useState(null);
  const [text, setText] = useState('');
  const [view, setView] = useState('form');
  const [lint, setLint] = useState(null);
  const [diff, setDiff] = useState(null);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    const r = await api.get(`admin/books/${version}`);
    if (r.kind !== 'ok') { setErr(r.error); return; }
    const b = r.body.book;
    setBook(b);
    setDraft(cloneBook(b));
    setText(JSON.stringify(serializeBook(b), null, 2));
    setView(b.status === 'draft' ? 'form' : 'json');
    setLint(null); setMsg(null); setErr(null);
    if (Number(version) > 1) {
      const d = await api.get(`admin/books/diff?from=${Number(version) - 1}&to=${version}`);
      setDiff(d.kind === 'ok' ? d.body : null);
    } else setDiff(null);
  }
  useEffect(() => { load(); }, [version]); // eslint-disable-line react-hooks/exhaustive-deps

  const editable = book && book.status === 'draft';

  // dirty vs the last-saved book, computed from whichever view is authoritative
  const dirty = useMemo(() => {
    if (!book || !editable) return false;
    if (view === 'json') {
      try { return !sameContent(JSON.parse(text), book); } catch { return true; }
    }
    return draft ? !sameContent(draft, book) : false;
  }, [book, editable, view, text, draft]);

  // ---- view sync ----
  function toJson() { setText(JSON.stringify(serializeBook(draft), null, 2)); setView('json'); setErr(null); }
  function toForm() {
    try { setDraft(JSON.parse(text)); setView('form'); setErr(null); }
    catch (e) { setErr('Cannot switch to Form — the JSON is invalid: ' + e.message); }
  }

  // ---- draft mutators (immutable) ----
  const patchBook = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const patchMain = (mi, patch) => setDraft((d) => ({ ...d, mains: d.mains.map((m, i) => (i === mi ? { ...m, ...patch } : m)) }));
  const patchSub = (mi, si, patch) => setDraft((d) => ({
    ...d, mains: d.mains.map((m, i) => (i === mi ? { ...m, subs: m.subs.map((s, j) => (j === si ? { ...s, ...patch } : s)) } : m)),
  }));
  const patchMatch = (mi, si, key, csv) => setDraft((d) => ({
    ...d, mains: d.mains.map((m, i) => (i === mi ? {
      ...m, subs: m.subs.map((s, j) => (j === si ? { ...s, match: { ...(s.match || {}), [key]: arrFromCsv(csv) } } : s)),
    } : m)),
  }));
  const addMain = () => setDraft((d) => ({ ...d, mains: [...d.mains, blankMain((d.mains.reduce((mx, m) => Math.max(mx, Number(m.sort) || 0), 0)) + 1)] }));
  const removeMain = (mi) => setDraft((d) => ({ ...d, mains: d.mains.filter((_, i) => i !== mi) }));
  const addSub = (mi) => setDraft((d) => ({
    ...d, mains: d.mains.map((m, i) => (i === mi ? { ...m, subs: [...(m.subs || []), blankSub((m.subs || []).reduce((mx, s) => Math.max(mx, Number(s.sort) || 0), 0) + 1)] } : m)),
  }));
  const removeSub = (mi, si) => setDraft((d) => ({
    ...d, mains: d.mains.map((m, i) => (i === mi ? { ...m, subs: m.subs.filter((_, j) => j !== si) } : m)),
  }));

  // ---- lifecycle actions ----
  const run = (fn) => async () => { setBusy(true); setMsg(null); setErr(null); try { await fn(); } catch (e) { setErr(e.message); } setBusy(false); };

  const save = run(async () => {
    let body;
    if (view === 'json') { try { body = JSON.parse(text); } catch (e) { setErr('Not valid JSON: ' + e.message); return; } }
    else body = serializeBook(draft);
    const r = await api.put(`admin/books/${version}`, body);
    if (r.kind === 'ok' && r.body.ok) {
      const b = r.body.book;
      setBook(b); setDraft(cloneBook(b)); setText(JSON.stringify(serializeBook(b), null, 2));
      setLint(r.body.lint); setMsg('Draft saved.');
    } else setErr((r.body && r.body.reason) || r.error); // replaceDraft refuses with `reason`
  });

  const validate = run(async () => {
    const r = await api.post(`admin/books/${version}/validate`, {});
    if (r.kind === 'ok') { setLint(r.body); setMsg(r.body.ok ? 'Lint clean — activatable.' : null); }
    else setErr(r.error);
  });

  const activate = run(async () => {
    const r = await api.post(`admin/books/${version}/activate`, {});
    if (r.kind === 'ok' && r.body.ok) { setMsg(`v${version} is ACTIVE — new quotes price from it now.`); load(); }
    else { setErr((r.body && r.body.reason) || r.error); if (r.body && r.body.lint) setLint(r.body.lint); }
  });

  if (err && !book) return <div><a className="crumb" href="/books" onClick={(e) => { e.preventDefault(); navigate('/books'); }}>‹ Rate card</a><Banner kind="err">{err}</Banner></div>;
  if (!book) return <p className="muted">Loading…</p>;

  return (
    <div>
      <div className="pagehead">
        <h1>
          <a className="crumb" href="/books" onClick={(e) => { e.preventDefault(); navigate('/books'); }}>Rate card</a> / v{book.version}
          {' '}<span className={'pill ' + book.status}>{book.status}</span>
          {dirty && <span className="pill warn" style={{ marginInlineStart: 6 }}>unsaved</span>}
        </h1>
        {editable && (
          <div className="tabs">
            <button className={view === 'form' ? 'active' : ''} onClick={() => view !== 'form' && toForm()}>Form</button>
            <button className={view === 'json' ? 'active' : ''} onClick={() => view !== 'json' && toJson()}>JSON</button>
          </div>
        )}
      </div>

      {!editable && <Banner kind="info">An activated version is history — immutable forever (jobs pinned to it settle under these rows). Draft a new version to retune.</Banner>}

      {diff && diff.changes.length > 0 && (
        <details className="card" open>
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Changes vs v{diff.from_version} ({diff.changes.length})</summary>
          <table style={{ marginBlockStart: 8 }}><tbody>
            {diff.changes.map((c, i) => (
              <tr key={i}><td className="mono" style={{ inlineSize: 150 }}>{c.kind}</td><td>{c.at}</td>
                <td className="mono muted">{JSON.stringify(c.from)} → {JSON.stringify(c.to)}</td></tr>
            ))}
          </tbody></table>
        </details>
      )}

      {editable && view === 'form'
        ? <FormEditor draft={draft} patchBook={patchBook} patchMain={patchMain} patchSub={patchSub}
            patchMatch={patchMatch} addMain={addMain} removeMain={removeMain} addSub={addSub} removeSub={removeSub} />
        : <textarea className="json" value={text} onChange={(e) => setText(e.target.value)} readOnly={!editable} />}

      <div className="actionbar">
        {editable && <button className="btn" disabled={busy} onClick={save}>Save draft</button>}
        <button className="btn" disabled={busy} onClick={validate}>Validate{dirty ? ' (stored)' : ''}</button>
        {editable && <button className="btn primary" disabled={busy || dirty || (lint && !lint.ok)}
          title={dirty ? 'Save your edits first' : (lint && !lint.ok ? 'Lint has errors — fix them first' : '')}
          onClick={activate}>Activate</button>}
        <button className="btn ghost" onClick={() => navigate('/books')}>Back</button>
        {dirty && editable && <span className="hint">unsaved edits — Save before you Validate/Activate</span>}
      </div>

      {msg && <Banner kind="ok">{msg}</Banner>}
      {err && <Banner kind="err">{err}</Banner>}

      {lint && (
        <div className="card">
          <h2 style={{ marginBlockStart: 0 }}>Lint {lint.ok ? '— clean ✓' : `— ${lint.errors.length} error(s)`}</h2>
          {lint.errors.map((e, i) => <p key={'e' + i} className="err" style={{ margin: '2px 0' }}>✗ {e}</p>)}
          {lint.warnings.map((w, i) => <p key={'w' + i} className="muted" style={{ margin: '2px 0' }}>⚠ {w}</p>)}
          {(lint.smoke || []).length > 0 && <div className="hint" style={{ margin: '8px 0 2px' }}>smoke-quotes (every menu row priced through the real engine)</div>}
          <div className="chips">
            {(lint.smoke || []).map((s) => (
              <span key={s.main_key} className="chip" style={s.ok ? {} : { color: 'var(--coral)', borderColor: 'var(--coral)' }}>
                {s.ok ? `${s.main_key}: ${fmtInt(s.credits)} cr` : `${s.main_key}: ${s.error}`}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// the friendly form editor
// ---------------------------------------------------------------------------------------

function FormEditor({ draft, patchBook, patchMain, patchSub, patchMatch, addMain, removeMain, addSub, removeSub }) {
  return (
    <div>
      <div className="card">
        <div className="grid" style={{ maxInlineSize: 620 }}>
          <Field label="Credits per USD (anchor)" hint="~1,000 credits ≈ $1 of priced work — prices everything">
            <input type="number" min="1" step="1" value={draft.credits_per_usd ?? ''} onChange={(e) => patchBook({ credits_per_usd: numOrNull(e.target.value) })} />
          </Field>
          <Field label="Version note" style={{ gridColumn: '2 / -1' }}>
            <input value={draft.note ?? ''} onChange={(e) => patchBook({ note: e.target.value })} placeholder="what this retune changes" />
          </Field>
        </div>
      </div>

      {(draft.mains || []).map((m, mi) => (
        <MainCard key={mi} m={m} mi={mi}
          patchMain={patchMain} patchSub={patchSub} patchMatch={patchMatch}
          addSub={addSub} removeSub={removeSub} removeMain={removeMain} />
      ))}

      <div className="row"><button className="btn tiny" onClick={addMain}>+ Add menu row (main)</button></div>
    </div>
  );
}

function MainCard({ m, mi, patchMain, patchSub, patchMatch, addSub, removeSub, removeMain }) {
  const isFixed = m.mode === 'fixed';
  const priceLabel = isFixed ? `${fmtInt(m.fixed_credits)} cr${m.per_unit ? ' · ' + m.per_unit : ''}` : 'cost-plus (Σ subs)';
  return (
    <details className="main-card">
      <summary>
        <span className="tw">▸</span>
        <span className="mono">{m.key || '(no key)'}</span>
        <span className="muted grow">{m.label || '—'}</span>
        <span className="chip">{m.mode}</span>
        <span className="mono muted">{priceLabel}</span>
        <span className="mono muted">· {(m.subs || []).length} sub{(m.subs || []).length === 1 ? '' : 's'}</span>
      </summary>
      <div className="body">
        <div className="grid" style={{ marginBlockStart: 12 }}>
          <Field label="Key" hint="the seam verb / edit class — changing it re-maps the menu row">
            <input className="mono" value={m.key ?? ''} onChange={(e) => patchMain(mi, { key: e.target.value })} />
          </Field>
          <Field label="Label"><input value={m.label ?? ''} onChange={(e) => patchMain(mi, { label: e.target.value })} /></Field>
          <Field label="Mode">
            <select value={m.mode} onChange={(e) => patchMain(mi, { mode: e.target.value })}>
              {MODE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
          {isFixed && (
            <Field label="Fixed credits"><input type="number" min="0" step="1" value={m.fixed_credits ?? ''} onChange={(e) => patchMain(mi, { fixed_credits: numOrNull(e.target.value) })} /></Field>
          )}
          {isFixed && (
            <Field label="Per unit" hint="multiplies the fixed price (per 15s block / per language); blank = per job">
              <select value={m.per_unit ?? ''} onChange={(e) => patchMain(mi, { per_unit: e.target.value || null })}>
                {PER_UNIT_OPTIONS.map((o) => <option key={o} value={o}>{o || '(per job)'}</option>)}
              </select>
            </Field>
          )}
          <Field label="Sort"><input type="number" step="1" value={m.sort ?? 0} onChange={(e) => patchMain(mi, { sort: e.target.value })} /></Field>
        </div>

        <div className="hint" style={{ margin: '12px 0 2px' }}>Atoms (subs)</div>
        {(m.subs || []).map((s, si) => (
          <SubCard key={si} s={s} onPatch={(p) => patchSub(mi, si, p)} onMatch={(k, v) => patchMatch(mi, si, k, v)} onRemove={() => removeSub(mi, si)} />
        ))}
        <div className="row" style={{ marginBlockStart: 8 }}>
          <button className="btn tiny" onClick={() => addSub(mi)}>+ Add atom</button>
          <span className="spacer" />
          <button className="btn ghost tiny" onClick={() => removeMain(mi)}>Remove this menu row</button>
        </div>
      </div>
    </details>
  );
}

function SubCard({ s, onPatch, onMatch, onRemove }) {
  const isExtra = s.position === 'extra';
  return (
    <div className="sub-card">
      <div className="subhead">
        <input className="mono" placeholder="key" value={s.key ?? ''} onChange={(e) => onPatch({ key: e.target.value })} style={{ inlineSize: 160 }} />
        <input placeholder="label" value={s.label ?? ''} onChange={(e) => onPatch({ label: e.target.value })} className="grow" />
        <button className="btn ghost tiny" onClick={onRemove}>remove</button>
      </div>
      <div className="grid">
        <Field label="Position">
          <select value={s.position} onChange={(e) => onPatch({ position: e.target.value })}>
            {POSITION_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Factor (cost-plus ×)" hint="included cost-plus / cost-plus extra; ≥1 (≥2 honors D23)">
          <input type="number" step="0.1" value={s.factor ?? ''} onChange={(e) => onPatch({ factor: numOrNull(e.target.value) })} />
        </Field>
        <Field label="Extra mode" hint="an extra (not-included) atom prices this way">
          <select value={s.extra_mode ?? ''} onChange={(e) => onPatch({ extra_mode: e.target.value || null })}>
            {EXTRA_MODE_OPTIONS.map((o) => <option key={o} value={o}>{o || '—'}</option>)}
          </select>
        </Field>
        {(isExtra || s.extra_mode === 'fixed') && (
          <Field label="Extra fixed credits"><input type="number" min="0" step="1" value={s.extra_fixed_credits ?? ''} onChange={(e) => onPatch({ extra_fixed_credits: numOrNull(e.target.value) })} /></Field>
        )}
        <Field label="Included qty (allowance)" hint="N free, then overflow priced by extra mode">
          <input type="number" min="0" step="1" value={s.included_qty ?? ''} onChange={(e) => onPatch({ included_qty: numOrNull(e.target.value) })} />
        </Field>
        <Field label="Unit"><input value={s.unit ?? ''} onChange={(e) => onPatch({ unit: e.target.value })} placeholder="e.g. idea_set" /></Field>
        <Field label="Sort"><input type="number" step="1" value={s.sort ?? 0} onChange={(e) => onPatch({ sort: e.target.value })} /></Field>
      </div>
      <div className="grid" style={{ marginBlockStart: 8 }}>
        {MATCH_KEYS.map((k) => (
          <Field key={k} label={'match: ' + k} hint={k === 'line_kinds' ? 'quote-side' : 'settle-side'}>
            <input className="mono" value={csvFromArr((s.match || {})[k])} placeholder="a, b" onChange={(e) => onMatch(k, e.target.value)} />
          </Field>
        ))}
      </div>
      <label className="check" style={{ marginBlockStart: 8 }}>
        <input type="checkbox" checked={s.settle_on_fail === true} onChange={(e) => onPatch({ settle_on_fail: e.target.checked })} />
        settle on fail <span className="hint">(policy change — day-one law is false everywhere; a failed job's cost is ours)</span>
      </label>
    </div>
  );
}
